import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import apiClient from '../api/client';
import PreviewRaceDashboardPage from '../components/predictionService/PreviewRaceDashboardPage';
import { buildRaceDetailModel } from '../components/raceDetail/utils';

const KEIRIN_TABLE_COLUMNS = [
  { key: 'horseNumber', label: '車番', align: 'center', type: 'number' },
  { key: 'horseName', label: '選手名', type: 'name' },
  { key: 'bracketNo', label: '枠番', align: 'center', type: 'text' },
  { key: 'predRank', label: '予測順位', align: 'right', type: 'rounded' },
  { key: 'winRate', label: '表示勝率', align: 'right', type: 'percent' },
  { key: 'rawProbabilityPct', label: '生確率', align: 'right', type: 'percent' },
  { key: 'aiRank', label: 'AI評価', align: 'center', type: 'rank' },
];

const EMPTY_PAGE_DATA = {
  availableDates: [],
  racesByDate: {},
  previewNotice: '競輪の最新予測データを準備しています。',
  raceNotice: '',
  helpBody:
    '競輪の最新予測JSONをもとに、riders を勝率分析へ、ev_bets を AI指数（予測確率 × オッズ）の高い三連単候補として表示します。',
};

const toNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const normalized = typeof value === 'string' ? value.replace(/,/g, '').trim() : value;
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
};

const formatGeneratedAt = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const deriveKeirinConfidence = (top3SharePct) => {
  if (top3SharePct >= 55) return 'A';
  if (top3SharePct >= 45) return 'B';
  return 'C';
};

const deriveKeirinRoughness = (top3SharePct) => {
  if (top3SharePct >= 55) return 'C';
  if (top3SharePct >= 45) return 'B';
  if (top3SharePct >= 35) return 'A';
  return 'S';
};

const buildRaceName = (race) =>
  `${race.place_name || ''}${race.race_label || (race.race_no ? `${race.race_no}R` : '')}`.trim() ||
  race.race_id;

const buildSurfaceLabel = (race) =>
  [race.race_class, race.race_round].filter(Boolean).join(' / ') || 'KEIRIN';

const buildRiderRows = (race) => {
  const sorted = [...(race.riders || [])].sort((a, b) => {
    const rankDiff = (toNumber(a.pred_rank) ?? 999) - (toNumber(b.pred_rank) ?? 999);
    if (rankDiff !== 0) return rankDiff;
    const probDiff = (toNumber(b.pred_prob_display) ?? 0) - (toNumber(a.pred_prob_display) ?? 0);
    if (probDiff !== 0) return probDiff;
    return (toNumber(a.bike_no) ?? 99) - (toNumber(b.bike_no) ?? 99);
  });

  return sorted.map((rider) => ({
    umaban: rider.bike_no,
    horse_name: rider.player_name || `車番 ${rider.bike_no ?? '-'}`,
    p: toNumber(rider.pred_prob_display),
    ev: toNumber(rider.pred_prob_raw),
    pick: (toNumber(rider.pred_rank) ?? 999) <= 3 ? 1 : 0,
    p_rank_in_race: toNumber(rider.pred_rank),
  }));
};

const buildFocusScores = (race) => {
  const scores = new Map();
  (race.ev_bets || []).forEach((bet) => {
    const edge = Math.max(toNumber(bet.edge) ?? 0, 0);
    [bet.first_bike, bet.second_bike, bet.third_bike].forEach((bikeNo) => {
      const key = String(bikeNo ?? '').trim();
      if (!key) return;
      scores.set(key, (scores.get(key) || 0) + edge);
    });
  });
  return scores;
};

const buildMetaText = (row, focusScore = 0) => {
  const segments = [
    row.bracketNo ? `枠番 ${row.bracketNo}` : null,
    row.predRank ? `予測 ${row.predRank}位` : null,
    row.winRate !== null ? `勝率 ${row.winRate.toFixed(1)}%` : null,
    row.rawProbabilityPct !== null ? `生確率 ${row.rawProbabilityPct.toFixed(1)}%` : null,
  ].filter(Boolean);
  if (focusScore > 0) {
    segments.push(`EV注目 ${focusScore.toFixed(2)}`);
  }
  return segments.join(' / ');
};

const buildEvBetsPanel = (race) => ({
  eyebrow: 'TRIFECTA EV',
  title: '三連単のAI指数上位',
  emptyText: 'AI指数が基準を超えた買い目はありません',
  bets: [...(race.ev_bets || [])]
    .map((bet, index) => ({
      rank: index + 1,
      combo: bet.combo,
      odds: toNumber(bet.odds),
      pHatPct:
        toNumber(bet.p_hat) !== null ? Number(((toNumber(bet.p_hat) || 0) * 100).toFixed(1)) : null,
      ev: toNumber(bet.ev),
      edge: toNumber(bet.edge),
    }))
    .sort((a, b) => {
      const evDiff = (b.ev ?? -999) - (a.ev ?? -999);
      if (evDiff !== 0) return evDiff;
      return (b.edge ?? -999) - (a.edge ?? -999);
    }),
});

const buildKeirinDetailModel = (race, raceDate) => {
  const tanpukuRows = buildRiderRows(race);
  if (!tanpukuRows.length) return null;

  const raceName = buildRaceName(race);
  const data = {
    tanpuku_df: tanpukuRows,
    umaren_df: [],
    sanrenpuku_df: [],
    sanrentan_df: [],
    race_name: raceName,
  };

  const initialModel = buildRaceDetailModel({
    data,
    selectedRaceMeta: {
      race_name: raceName,
      race_key: race.race_id,
      surface_label: buildSurfaceLabel(race),
    },
    selectedDate: raceDate,
  });
  if (!initialModel) return null;

  const top3SharePct = initialModel.raceConfidence?.top3SharePct ?? 0;
  const roughness = deriveKeirinRoughness(top3SharePct);
  const confidence = deriveKeirinConfidence(top3SharePct);
  const detailModel =
    buildRaceDetailModel({
      data,
      selectedRaceMeta: {
        race_name: raceName,
        race_key: race.race_id,
        surface_label: buildSurfaceLabel(race),
        roughness_raw: roughness,
        confidence_raw: confidence,
      },
      selectedDate: raceDate,
    }) || initialModel;

  const riderMap = new Map(
    (race.riders || []).map((rider) => [String(rider.bike_no ?? ''), rider])
  );
  const focusScores = buildFocusScores(race);

  const enrichHorse = (horse) => {
    const rider = riderMap.get(String(horse.horseNumber ?? '')) || {};
    const focusScore = focusScores.get(String(horse.horseNumber ?? '')) || 0;
    return {
      ...horse,
      odds: null,
      baseOdds: null,
      bracketNo: rider.bracket_no ?? '-',
      predRank: toNumber(rider.pred_rank),
      rawProbabilityPct:
        toNumber(rider.pred_prob_raw) !== null
          ? Number(((toNumber(rider.pred_prob_raw) || 0) * 100).toFixed(1))
          : null,
      nameSubtext: [rider.bracket_no ? `枠番 ${rider.bracket_no}` : null, rider.pred_rank ? `予測 ${rider.pred_rank}位` : null]
        .filter(Boolean)
        .join(' / '),
      metaText: buildMetaText(
        {
          bracketNo: rider.bracket_no,
          predRank: toNumber(rider.pred_rank),
          winRate: horse.winRate ?? null,
          rawProbabilityPct:
            toNumber(rider.pred_prob_raw) !== null
              ? Number(((toNumber(rider.pred_prob_raw) || 0) * 100).toFixed(1))
              : null,
        },
        focusScore
      ),
      focusScore,
    };
  };

  const horses = detailModel.horses.map(enrichHorse);
  const recommendedHorses = detailModel.recommendedHorses
    .map(enrichHorse)
    .slice(0, 3);
  const focusHorses = [...horses]
    .sort((a, b) => {
      const focusDiff = (b.focusScore ?? 0) - (a.focusScore ?? 0);
      if (focusDiff !== 0) return focusDiff;
      return (b.winRate ?? 0) - (a.winRate ?? 0);
    })
    .slice(0, 3);

  return {
    ...detailModel,
    title: `${raceName}のAI分析`,
    subtitle: '競輪AIデータ解析による予想分析',
    surfaceLabel: buildSurfaceLabel(race),
    roughness,
    raceConfidence: {
      ...(detailModel.raceConfidence || {}),
      label: confidence,
      tone: confidence.toLowerCase(),
      top3SharePct,
    },
    horses,
    recommendedHorses,
    focusHorses,
    chartTitle: `勝率分析（%） 上位${Math.min(horses.length, 7)}車`,
    chartData: horses.slice(0, 7),
    chartOddsLabel: '単勝オッズ',
    recommendTitle: 'AI推奨選手',
    focusTitle: 'AI注目選手',
    tableTitle: '出走選手データ',
    tableColumns: KEIRIN_TABLE_COLUMNS,
    stats: [
      { label: '分析人数', value: `${horses.length}車` },
      { label: '発走', value: race.start_time || '-' },
      { label: 'レース格', value: race.race_class || '-' },
      { label: 'トップ勝率', value: detailModel.stats?.[1]?.value || '-' },
      { label: '上位3車シェア', value: `${top3SharePct.toFixed(1)}%` },
      { label: 'EV買い目数', value: `${(race.ev_bets || []).length}点` },
    ],
    evBetsPanel: buildEvBetsPanel(race),
  };
};

const buildKeirinPageData = (payload) => {
  const targetDate = payload?.target_date || '';
  const racesByDate = {};

  (payload?.races || []).forEach((race) => {
    const pushRace = (entry) => {
      if (!racesByDate[targetDate]) {
        racesByDate[targetDate] = [];
      }
      racesByDate[targetDate].push(entry);
    };

    // 未契約のレースはサーバが riders / ev_bets を空にして返す（#34）。捨てると一覧から
    // 消えてしまうので、ロックの札を付けたまま残す（#36）
    if (race.locked) {
      pushRace({
        race_key: race.race_id,
        race_name: buildRaceName(race),
        place_name: race.place_name || '',
        race_no: race.race_no,
        start_time: race.start_time || '',
        locked: true,
        is_free: false,
        detailModel: null,
      });
      return;
    }

    const detailModel = buildKeirinDetailModel(race, targetDate);
    if (!detailModel) return;
    pushRace({
      locked: false,
      is_free: Boolean(race.is_free),
      race_key: race.race_id,
      race_name: buildRaceName(race),
      place_name: race.place_name || '',
      race_no: race.race_no,
      start_time: race.start_time || '',
      surface_label: buildSurfaceLabel(race),
      roughness_raw: detailModel.roughness,
      confidence_raw: detailModel.raceConfidence?.label || null,
      detailModel,
    });
  });

  Object.values(racesByDate).forEach((raceList) => {
    raceList.sort((a, b) => {
      const placeDiff = String(a.place_name || '').localeCompare(String(b.place_name || ''), 'ja');
      if (placeDiff !== 0) return placeDiff;
      return (toNumber(a.race_no) ?? 99) - (toNumber(b.race_no) ?? 99);
    });
  });

  const availableDates = targetDate && racesByDate[targetDate] ? [targetDate] : [];
  const generatedLabel = formatGeneratedAt(payload?.generated_at);

  return {
    availableDates,
    racesByDate,
    previewNotice: payload?.file_exists
      ? `競輪の最新予測JSONを表示しています。${generatedLabel ? `最終生成 ${generatedLabel}` : ''}`
      : '競輪の最新予測JSONがまだ配置されていません。',
    raceNotice:
      !payload?.file_exists
        ? 'サーバー上の最新JSONが未配置のため、競輪ページはまだ表示できません。'
        : availableDates.length === 0
          ? '現在表示できる競輪レースがありません。'
          : '',
    helpBody:
      '競輪の fixed JSON を読み込み、riders は pred_prob_display を勝率表示に、pred_prob_raw は分析補助値として表示します。ev_bets は AI指数（予測確率 × オッズ）の閾値を通過した三連単候補です。',
  };
};

function KeirinModelRaceList() {
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(EMPTY_PAGE_DATA);

  useEffect(() => {
    let cancelled = false;

    const fetchLivePredictions = async () => {
      setLoading(true);
      try {
        // apiClient がアクセストークンを載せる。未ログインでも無料枠は 200 で返る（#36）
        const response = await apiClient.get('/api/keirin_model/live_predictions');
        if (!cancelled) {
          setPageData(buildKeirinPageData(response.data));
        }
      } catch (error) {
        console.error('競輪JSON取得失敗:', error);
        if (!cancelled) {
          setPageData({
            ...EMPTY_PAGE_DATA,
            raceNotice: '競輪の最新予測JSON取得に失敗しました。',
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchLivePredictions();

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          bgcolor: '#050c16',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PreviewRaceDashboardPage
      activeServiceKey="keirin"
      selectorEyebrow="Race Selector"
      selectorTitle="競輪レース詳細を選択"
      helpTitle="競輪版の表示について"
      availableDates={pageData.availableDates}
      racesByDate={pageData.racesByDate}
      previewNotice={pageData.previewNotice}
      raceNotice={pageData.raceNotice}
      helpBody={pageData.helpBody}
    />
  );
}

export default KeirinModelRaceList;
